<template>
  <div class="iq-loader-wrapper" v-if="isLoading || isExiting">
    <div class="loader-container" :class="{ 'fade-out': isExiting }">
      <div class="rotating-arc" :style="arcStyle"></div>
      <div class="rotating-arc-reverse" :style="arcReverseStyle"></div>
      <div class="glow-circle" :style="glowStyle"></div>
      <div class="logo-wrapper" :class="{ 'scale-in': isEntering }">
        <img
          class="logo rounded-lg"
          src="/img/iq.png"
          alt="IQ Platform"
          :style="logoStyle"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PageLoaderNew",
  data() {
    return {
      isEntering: true,
      isExiting: false,
      exitTimeout: null,
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters.getPageLoading;
    },
    primaryColor() {
      return "#9900ff";
    },
    secondaryColor() {
      return "#c32c80";
    },
    arcStyle() {
      return {
        borderTopColor: this.primaryColor,
        borderRightColor: this.primaryColor,
        filter: `drop-shadow(0 0 6px ${this.primaryColor}80)`,
      };
    },
    arcReverseStyle() {
      return {
        borderBottomColor: this.secondaryColor,
        borderLeftColor: this.secondaryColor,
        filter: `drop-shadow(0 0 6px ${this.secondaryColor}80)`,
      };
    },
    glowStyle() {
      return {
        background: `radial-gradient(circle, ${this.primaryColor}26 0%, ${this.secondaryColor}14 50%, transparent 100%)`,
      };
    },
    logoStyle() {
      return {
        filter: `drop-shadow(0 4px 12px ${this.primaryColor}40)`,
      };
    },
  },
  watch: {
    isLoading(newVal) {
      if (!newVal && !this.isExiting) {
        // Loading finished, start exit animation
        this.isEntering = false;
        this.isExiting = true;
        this.exitTimeout = setTimeout(() => {
          this.isExiting = false;
        }, 800);
      } else if (newVal) {
        // Loading started again
        this.isEntering = true;
        this.isExiting = false;
        if (this.exitTimeout) {
          clearTimeout(this.exitTimeout);
        }
      }
    },
  },
  beforeDestroy() {
    if (this.exitTimeout) {
      clearTimeout(this.exitTimeout);
    }
  },
};
</script>

<style scoped>
.iq-loader-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  pointer-events: auto;
  cursor: wait;
}

.iq-loader-wrapper::before {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.01);
  backdrop-filter: blur(0.5px);
}

.loader-container {
  position: relative;
  width: min(150px, 22vw);
  height: min(150px, 22vw);
  transition: opacity 0.6s ease, transform 0.6s ease;
  filter: drop-shadow(0 6px 20px rgba(115, 34, 176, 0.15));
}

.loader-container.fade-out {
  opacity: 0;
  transform: scale(0.7);
}

.rotating-arc {
  position: absolute;
  inset: 6px;
  border-radius: 50%;
  border: 3px solid transparent;
  animation: rotateArc 2.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.rotating-arc-reverse {
  position: absolute;
  inset: 18px;
  border-radius: 50%;
  border: 3px solid transparent;
  animation: rotateArcReverse 3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.glow-circle {
  position: absolute;
  inset: 32px;
  border-radius: 50%;
  animation: pulse 3s ease-in-out infinite;
}

.logo-wrapper {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(0);
  perspective: 1000px;
}

.logo-wrapper.scale-in {
  animation: scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.logo {
  width: min(60px, 10vw);
  height: auto;
  animation: float3d 4s ease-in-out infinite;
  transform-style: preserve-3d;
}

@keyframes rotateArc {
  0% {
    transform: rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: rotate(360deg);
    opacity: 1;
  }
}

@keyframes rotateArcReverse {
  0% {
    transform: rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: rotate(-360deg);
    opacity: 1;
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.15);
    opacity: 0.4;
  }
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes float3d {
  0%,
  100% {
    transform: translateZ(0px) scale(1);
    filter: drop-shadow(0 4px 12px rgba(115, 34, 176, 0.25));
  }
  50% {
    transform: translateZ(50px) scale(1.12);
    filter: drop-shadow(0 12px 24px rgba(115, 34, 176, 0.5));
  }
}
</style>
